import { Check, Circle, Loader2, AlertTriangle } from 'lucide-react';

import { cn, sectionNumber } from '@/lib/utils';
import type { AgentId } from '@/hooks/useJobProgress';
import { AgentAvatar, agentFullName } from './AgentAvatar';

interface AgentSidebarProps {
  /** Convex jobs.state value. */
  jobState: string;
  /** Currently-selected agent for the right-pane panel. */
  selected: AgentId | 'delivery';
  onSelect: (agent: AgentId | 'delivery') => void;
  /** True if the job is in an error state (state==='failed'). */
  hasError: boolean;
}

/**
 * The pipeline view's left sidebar. Lists the 7 agents in pipeline
 * order plus a "Delivery" tile at the bottom. Each row shows a status
 * icon derived from the live job state.
 */
const AGENT_ORDER: AgentId[] = ['ora', 'scry', 'loom', 'lex', 'lumen', 'mosaic', 'forge'];

type AgentStatus = 'pending' | 'running' | 'awaiting_user' | 'done' | 'failed';

/**
 * Map the job's current state to per-agent status.
 *
 * The job state machine in convex/lib/types.ts has 16 states; we
 * collapse them per-agent here.
 */
function statusForAgent(agent: AgentId, jobState: string, hasError: boolean): AgentStatus {
  if (hasError) return 'failed';

  // Compute pipeline progress as an ordered index. The pre-FORGE
  // checkpoint counts as "after LUMEN/MOSAIC and before FORGE":
  // earlier agents are done, FORGE itself is awaiting user input.
  const stagesAfter: Record<AgentId, string[]> = {
    ora: [
      'scry_running', 'scry_awaiting_review',
      'loom_running', 'loom_lex_review', 'loom_awaiting_review',
      'lumen_running', 'mosaic_running', 'lumen_awaiting_review',
      'forge_awaiting_pre_input',
      'forge_running', 'forge_lex_review', 'completed',
    ],
    scry: [
      'loom_running', 'loom_lex_review', 'loom_awaiting_review',
      'lumen_running', 'mosaic_running', 'lumen_awaiting_review',
      'forge_awaiting_pre_input',
      'forge_running', 'forge_lex_review', 'completed',
    ],
    loom: [
      'lumen_running', 'mosaic_running', 'lumen_awaiting_review',
      'forge_awaiting_pre_input',
      'forge_running', 'forge_lex_review', 'completed',
    ],
    lex: [
      'lumen_running', 'mosaic_running', 'lumen_awaiting_review',
      'forge_awaiting_pre_input',
      'forge_running', 'forge_lex_review', 'completed',
    ],
    lumen: ['forge_awaiting_pre_input', 'forge_running', 'forge_lex_review', 'completed'],
    mosaic: ['forge_awaiting_pre_input', 'forge_running', 'forge_lex_review', 'completed'],
    forge: ['completed'],
  };

  const isDone = stagesAfter[agent].includes(jobState);
  if (isDone) return 'done';

  // Awaiting user.
  const awaitingMap: Record<AgentId, string[]> = {
    ora: ['ora_awaiting_review', 'ora_ambiguity_probe'],
    scry: ['scry_awaiting_review'],
    loom: ['loom_awaiting_review'],
    lex: [],
    lumen: ['lumen_awaiting_review'],
    mosaic: [],
    forge: ['forge_awaiting_pre_input'],
  };
  if (awaitingMap[agent].includes(jobState)) return 'awaiting_user';

  // Currently running.
  const runningMap: Record<AgentId, string[]> = {
    ora: ['ora_running'],
    scry: ['scry_running'],
    loom: ['loom_running'],
    lex: ['loom_lex_review', 'forge_lex_review'],
    lumen: ['lumen_running'],
    mosaic: ['mosaic_running'],
    forge: ['forge_running'],
  };
  if (runningMap[agent].includes(jobState)) return 'running';

  return 'pending';
}

export function AgentSidebar({ jobState, selected, onSelect, hasError }: AgentSidebarProps) {
  const isDelivered = jobState === 'completed';

  return (
    <nav aria-label="Pipeline agents" className="space-y-1.5">
      <p className="smallcaps mb-3 text-graphite">Agents</p>
      <ol className="space-y-0.5">
        {AGENT_ORDER.map((agent, idx) => {
          const status = statusForAgent(agent, jobState, hasError);
          const isSelected = selected === agent;
          return (
            <li key={agent}>
              <button
                type="button"
                onClick={() => onSelect(agent)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-sm py-1.5 px-2 text-left transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre',
                  isSelected ? 'bg-ink/5' : 'hover:bg-ink/[0.03]',
                )}
                aria-current={isSelected ? 'step' : undefined}
              >
                <span className="font-mono text-[10px] text-graphite tabular-nums w-6">
                  {sectionNumber(idx + 1)}
                </span>
                <AgentAvatar agent={agent} size="sm" />
                <span
                  className={cn(
                    'flex-1 truncate font-mono text-[11px]',
                    isSelected ? 'text-ink' : 'text-graphite',
                  )}
                >
                  {agentFullName(agent)}
                </span>
                <StatusGlyph status={status} />
              </button>
            </li>
          );
        })}
      </ol>

      <div className="my-3 border-t border-ink/10" />

      <button
        type="button"
        onClick={() => onSelect('delivery')}
        disabled={!isDelivered}
        className={cn(
          'flex w-full items-center gap-3 rounded-sm py-1.5 px-2 text-left transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre',
          selected === 'delivery' ? 'bg-ink/5' : 'hover:bg-ink/[0.03]',
          !isDelivered && 'cursor-not-allowed opacity-40',
        )}
        aria-current={selected === 'delivery' ? 'step' : undefined}
      >
        <span className="font-mono text-[10px] text-graphite tabular-nums w-6">
          {sectionNumber(8)}
        </span>
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-sm border border-ink/20 bg-paper-2 font-mono text-[9px] tracking-widest text-ink/80">
          DL
        </span>
        <span
          className={cn(
            'flex-1 truncate font-mono text-[11px]',
            selected === 'delivery' ? 'text-ink' : 'text-graphite',
          )}
        >
          DELIVERY
        </span>
        {isDelivered ? <Check className="h-3.5 w-3.5 text-moss" aria-hidden /> : null}
      </button>
    </nav>
  );
}

function StatusGlyph({ status }: { status: AgentStatus }) {
  if (status === 'done') return <Check className="h-3.5 w-3.5 text-moss" aria-hidden />;
  if (status === 'awaiting_user')
    return <Circle className="h-3.5 w-3.5 text-ochre" strokeWidth={2.5} aria-hidden />;
  if (status === 'running')
    return <Loader2 className="h-3.5 w-3.5 text-ochre animate-spin" aria-hidden />;
  if (status === 'failed')
    return <AlertTriangle className="h-3.5 w-3.5 text-vermilion" aria-hidden />;
  return <Circle className="h-3.5 w-3.5 text-graphite/40" aria-hidden />;
}
